import { Button, Card, Checkbox, Col, Form, InputNumber, Row, Select } from "antd";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { SharedQuota, StudyProgramQuota, StudyProgram } from "@bindeleddet/core/types";
import React, { useEffect, useState } from "react";
import { findDuplicateQuotaCombination } from "helpers/quotaValidation";

interface Props {
  sharedQuotas: SharedQuota[];
  studyProgramQuotas: StudyProgramQuota[];
  handleQuotas: (sq: SharedQuota[], spq: StudyProgramQuota[], csq: SharedQuota | undefined) => void;
  eventId: number;
}

const StudyProgramQuotaStep: React.FC<Props> = ({ sharedQuotas, studyProgramQuotas, handleQuotas, eventId }) => {
  const [form] = Form.useForm();
  const { Option } = Select;
  const [studyPrograms, setStudyPrograms] = useState<StudyProgram[]>([]);
  const [selectedQuotaId, setSelectedQuotaId] = useState<string | number | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  // Watch form values for reactive validation
  const studyProgramme = Form.useWatch("study_programme", form);
  const yearLevelsAccepted = Form.useWatch("year_levels_accepted", form);
  const studentsAllowed = Form.useWatch("students_allowed", form);

  useEffect(() => {
    if (studyPrograms.length > 0) return;
    axios.get<StudyProgram[]>(`/students/study-program/`).then(response => {
      const allPrograms = response.data.find(program => program.id === 0);
      const indok = response.data.find(program => program.id === 1);
      const others = response.data.find(program => program.id === 17);
      const restOfPrograms = response.data.filter(program => program.id !== 0 && program.id !== 1 && program.id !== 17);
      const sortedStudyPrograms = [
        ...(allPrograms ? [allPrograms] : []),
        ...(indok ? [indok] : []),
        ...restOfPrograms,
        ...(others ? [others] : []),
      ];
      setStudyPrograms(sortedStudyPrograms);
    });
  }, [studyPrograms]);

  // Reset selectedQuotaId if the selected quota was deleted
  useEffect(() => {
    if (selectedQuotaId && selectedQuotaId !== "new") {
      const quotaExists = sharedQuotas.some(q => q.id === Number(selectedQuotaId));
      if (!quotaExists) {
        setSelectedQuotaId(undefined);
      }
    }
  }, [sharedQuotas, selectedQuotaId]);

  // Revalidate fields when sharedQuotas or studyProgramQuotas change (e.g., after saving in step 2)
  // Only validate fields that have values to avoid showing errors on empty fields
  useEffect(() => {
    const fieldsToValidate: string[] = [];

    // Only validate students_allowed if it has a value
    const studentsAllowedValue = form.getFieldValue("students_allowed");
    if (studentsAllowedValue !== undefined && studentsAllowedValue !== null && studentsAllowedValue !== "") {
      fieldsToValidate.push("students_allowed");
    }

    // Only validate year_levels_accepted if it has values
    const yearLevelsValue = form.getFieldValue("year_levels_accepted");
    if (yearLevelsValue && Array.isArray(yearLevelsValue) && yearLevelsValue.length > 0) {
      fieldsToValidate.push("year_levels_accepted");
    }

    // Only validate study_programme if it has a value
    const studyProgrammeValue = form.getFieldValue("study_programme");
    if (studyProgrammeValue !== undefined && studyProgrammeValue !== null) {
      fieldsToValidate.push("study_programme");
    }

    if (fieldsToValidate.length > 0) {
      form.validateFields(fieldsToValidate).catch(() => {
        // Ignore validation errors, just trigger revalidation
      });
    }
  }, [sharedQuotas, studyProgramQuotas, form]);

  // Revalidate students_allowed when selected quota changes (only if field has value)
  useEffect(() => {
    const studentsAllowedValue = form.getFieldValue("students_allowed");
    if (studentsAllowedValue !== undefined && studentsAllowedValue !== null && studentsAllowedValue !== "") {
      form.validateFields(["students_allowed"]).catch(() => {
        // Ignore validation errors, just trigger revalidation
      });
    }
  }, [selectedQuotaId, form]);

  // Revalidate year_levels_accepted when study_programme or selected quota changes (only if field has value)
  useEffect(() => {
    const yearLevelsValue = form.getFieldValue("year_levels_accepted");
    if (
      studyProgramme &&
      selectedQuotaId &&
      selectedQuotaId !== "new" &&
      yearLevelsValue &&
      Array.isArray(yearLevelsValue) &&
      yearLevelsValue.length > 0
    ) {
      form.validateFields(["year_levels_accepted"]).catch(() => {
        // Ignore validation errors, just trigger revalidation
      });
    }
  }, [studyProgramme, selectedQuotaId, form]);

  const onFinish = (values: StudyProgramQuota) => {
    const saveQuota = async () => {
      setLoading(true);
      try {
        const isNewQuota = selectedQuotaId === "new";
        let sharedQuota: SharedQuota | undefined;

        if (isNewQuota) {
          // Create a new shared quota
          sharedQuota = await axios
            .post<SharedQuota>(`/events/shared-quotas/`, {
              max_students_allowed: values.students_allowed,
              event: eventId,
            })
            .then(response => response.data);
        } else {
          // Use existing shared quota
          sharedQuota = sharedQuotas.find(q => q.id === Number(selectedQuotaId));
        }

        if (!sharedQuota) {
          throw new Error("Kvote ikke funnet");
        }

        // Create study program quota
        const studyProgramQuotaResponse = await axios.post<StudyProgramQuota>(
          `/events/${eventId}/study-program-quotas/`,
          {
            ...values,
            shared_quota: sharedQuota.id,
          }
        );

        const newStudyProgramQuota = studyProgramQuotaResponse.data;

        handleQuotas(
          isNewQuota ? [...sharedQuotas, sharedQuota] : sharedQuotas,
          [...studyProgramQuotas, newStudyProgramQuota],
          undefined
        );

        form.resetFields();
        setSelectedQuotaId(undefined);
        message("success", isNewQuota ? "Kvote er opprettet og linje lagt til." : "Linjen er lagt til i kvoten");
        setLoading(false);
      } catch (error) {
        message(
          "error",
          selectedQuotaId === "new" ? "Noe gikk galt ved opprettelse av kvote." : "Noe gikk galt ved lagring av linje."
        );
        setLoading(false);
      }
    };

    saveQuota();
  };

  // Check if students_allowed validation passes
  const checkStudentsAllowedValid = () => {
    if (!studentsAllowed) {
      return false;
    }
    if (studentsAllowed <= 0) {
      return false;
    }
    const currentSelectedQuota =
      selectedQuotaId && selectedQuotaId !== "new"
        ? sharedQuotas.find(q => q.id === Number(selectedQuotaId))
        : undefined;
    return !(currentSelectedQuota && studentsAllowed > currentSelectedQuota.max_students_allowed);
  };

  // Check if year_levels_accepted validation passes (no duplicate combinations)
  const checkYearLevelsValid = () => {
    if (!yearLevelsAccepted || !Array.isArray(yearLevelsAccepted) || yearLevelsAccepted.length === 0) {
      return false;
    }
    if (studyProgramme === undefined || studyProgramme === null) {
      return false;
    }
    if (!selectedQuotaId || selectedQuotaId === "new") {
      return true; // New quota, no duplicates to check
    }

    // Check if this exact combination already exists in the selected quota
    const currentSelectedQuotaId = Number(selectedQuotaId);
    const existingQuotas = studyProgramQuotas.filter(
      spq => spq.shared_quota === currentSelectedQuotaId && spq.study_programme === studyProgramme
    );

    // Sort both arrays for comparison
    const sortedNewYearLevels = [...yearLevelsAccepted].sort((a, b) => a - b);
    const sortedNewYearLevelsStr = JSON.stringify(sortedNewYearLevels);

    // Check if any existing quota has the exact same year levels
    const hasExactMatch = existingQuotas.some(existingQuota => {
      const sortedExistingYearLevels = [...existingQuota.year_levels_accepted].sort((a, b) => a - b);
      const sortedExistingYearLevelsStr = JSON.stringify(sortedExistingYearLevels);
      return sortedNewYearLevelsStr === sortedExistingYearLevelsStr;
    });

    return !hasExactMatch;
  };

  // Check if form is valid and quota is selected
  const isFormValid =
    studyProgramme !== undefined &&
    yearLevelsAccepted !== undefined &&
    Array.isArray(yearLevelsAccepted) &&
    yearLevelsAccepted.length > 0 &&
    checkYearLevelsValid() &&
    checkStudentsAllowedValid() &&
    selectedQuotaId !== undefined &&
    selectedQuotaId !== "";

  return (
    <Card title="1. Plasser per trinn/linje">
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        requiredMark={false}
        validateTrigger={["onChange", "onBlur", "onSubmit"]}
      >
        <Row gutter={10}>
          <Col sm={12} xs={24}>
            <Form.Item
              name="study_programme"
              label="Linje"
              rules={[{ required: true, message: "Linje er ikke valgt." }]}
            >
              <Select
                showSearch
                placeholder="Velg linje..."
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children?.toString().toLowerCase().indexOf(input.toLowerCase()) ?? -1) >= 0
                }
                style={{ width: "100%" }}
              >
                {studyPrograms.map(studyProgram => (
                  <Option key={studyProgram.id} value={studyProgram.id}>
                    {studyProgram.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item
              name="year_levels_accepted"
              label="Årskull"
              validateTrigger={["onChange"]}
              rules={[
                { required: true, message: "Minst et årskull må legges til." },
                {
                  validator: (_, value) => {
                    // Only validate if we have all required fields
                    if (!value || !Array.isArray(value) || value.length === 0) {
                      return Promise.resolve();
                    }
                    if (studyProgramme === undefined || studyProgramme === null) {
                      return Promise.resolve();
                    }
                    if (!selectedQuotaId || selectedQuotaId === "new") {
                      return Promise.resolve();
                    }

                    // Check if this exact combination already exists in the selected quota
                    const currentSelectedQuotaId = Number(selectedQuotaId);
                    const existingQuotas = studyProgramQuotas.filter(
                      spq => spq.shared_quota === currentSelectedQuotaId && spq.study_programme === studyProgramme
                    );

                    // Create a temporary quota object for comparison
                    const tempQuota = {
                      study_programme: studyProgramme,
                      year_levels_accepted: value,
                    };

                    const exactMatchQuota = findDuplicateQuotaCombination(tempQuota, existingQuotas);

                    if (exactMatchQuota) {
                      return Promise.reject(
                        new Error(
                          `Denne nøyaktige kombinasjonen av linje og årskull finnes allerede i den valgte kvoten.`
                        )
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Checkbox.Group style={{ width: "100%" }}>
                <Row>
                  <Col span={5}>
                    <Checkbox value={1}>1</Checkbox>
                  </Col>
                  <Col span={5}>
                    <Checkbox value={2}>2</Checkbox>
                  </Col>
                  <Col span={5}>
                    <Checkbox value={3}>3</Checkbox>
                  </Col>
                  <Col span={5}>
                    <Checkbox value={4}>4</Checkbox>
                  </Col>
                  <Col span={4}>
                    <Checkbox value={5}>5</Checkbox>
                  </Col>
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </Col>
        </Row>
        <Row style={{ margin: "15px 0" }} gutter={10}>
          <Col md={12} xs={24}>
            <Form.Item
              name="students_allowed"
              label="Maks antall plasser"
              validateTrigger={["onBlur", "onChange"]}
              rules={[
                {
                  validator: (_, value) => {
                    // Only validate if user has entered something
                    if (value === undefined || value === null || value === "") {
                      return Promise.resolve();
                    }
                    if (value <= 0) {
                      return Promise.reject(new Error("Antall plasser må være mer enn 0."));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value <= 0) {
                      return Promise.resolve();
                    }
                    const currentSelectedQuota =
                      selectedQuotaId && selectedQuotaId !== "new"
                        ? sharedQuotas.find(q => q.id === Number(selectedQuotaId))
                        : undefined;
                    if (currentSelectedQuota && value > currentSelectedQuota.max_students_allowed) {
                      return Promise.reject(
                        new Error(
                          `Antall plasser kan ikke være høyere enn totalt antall plasser i valgte kvote (${currentSelectedQuota.max_students_allowed})`
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col md={8} xs={24}>
            <Form.Item label="Kvote å legge til linje i" rules={[{ required: true, message: "Velg en kvote" }]}>
              <Select
                value={selectedQuotaId}
                onChange={value => setSelectedQuotaId(value)}
                placeholder="-- Velg kvote å legge til linje i --"
                style={{ width: "100%" }}
              >
                <Option value={undefined} disabled>
                  -- Velg kvote å legge til linje i --
                </Option>
                <Option value="new">Ny kvote</Option>
                {sharedQuotas.map((quota, index) => (
                  <Option key={quota.id} value={quota.id}>
                    Kvote {index + 1}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col md={4} xs={24}>
            <Form.Item label=" ">
              <Button
                type="primary"
                style={{ width: "100%" }}
                htmlType="submit"
                loading={loading}
                disabled={!isFormValid}
              >
                Legg til
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default StudyProgramQuotaStep;
